import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Heart, Shield, Users, Calendar, ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BookingWizard from '@/components/BookingWizard';

const LandingPage = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hidden keyboard shortcut: Ctrl+X opens the admin login
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey && (e.key === 'x' || e.key === 'X')) {
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
        e.preventDefault();
        sessionStorage.setItem('admin_access', 'granted');
        window.location.href = '/admin';
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/logo-transparent.png" 
                alt="Großstadtherzen Logo" 
                className="h-12 md:h-14 w-auto"
              />
              <div className="hidden sm:block">
                <span className="font-['Merriweather'] font-bold text-base md:text-lg text-slate-900 leading-tight">
                  Ambulante Pflege<br />Großstadtherzen<sup>®</sup>
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a 
                href="https://www.grossstadtherzen.de/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-[#40E0D0] font-medium transition-colors"
                data-testid="nav-homepage"
              >
                Zum Pflegedienst
              </a>
              <button 
                onClick={() => scrollToSection('info')}
                className="text-slate-600 hover:text-[#40E0D0] font-medium transition-colors"
                data-testid="nav-info"
              >
                Pflegeberatung
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-slate-600 hover:text-[#40E0D0] font-medium transition-colors"
                data-testid="nav-contact"
              >
                Kontakt
              </button>
              <Button 
                onClick={() => {
                  setShowBooking(true);
                  scrollToSection('booking');
                }}
                className="bg-[#40E0D0] text-slate-900 hover:bg-[#3BCCC0] font-bold px-6 py-2 rounded-full"
                data-testid="nav-cta-button"
              >
                Jetzt Termin buchen
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <div className="flex flex-col gap-4">
                <a 
                  href="https://www.grossstadtherzen.de/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-left text-slate-600 hover:text-[#40E0D0] font-medium py-2"
                >
                  Zum Pflegedienst
                </a>
                <button 
                  onClick={() => scrollToSection('info')}
                  className="text-left text-slate-600 hover:text-[#40E0D0] font-medium py-2"
                >
                  Pflegeberatung
                </button>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="text-left text-slate-600 hover:text-[#40E0D0] font-medium py-2"
                >
                  Kontakt
                </button>
                <Button 
                  onClick={() => {
                    setShowBooking(true);
                    scrollToSection('booking');
                    setMobileMenuOpen(false);
                  }}
                  className="bg-[#40E0D0] text-slate-900 hover:bg-[#3BCCC0] font-bold w-full py-3 rounded-full"
                >
                  Jetzt Termin buchen
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left Content */}
            <div className="order-2 md:order-1 slide-up">
              <div className="inline-flex items-center gap-2 bg-[#E0F7FA] text-slate-700 px-5 py-3 rounded-full text-base md:text-lg font-semibold mb-6">
                <Shield className="w-5 h-5 text-[#40E0D0]" />
                Pflegeberatung nach §37,3 SGB XI
              </div>
              
              <h1 className="font-['Merriweather'] text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.4] mb-6">
                Professionelle Pflegeberatung bequem
                <br />
                <span className="inline-block mt-2">von <span className="text-[#40E0D0]">Zuhause</span></span>
              </h1>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
                Wir unterstützen Sie und Ihre Angehörigen in ganz Sachsen mit persönlicher 
                Telefon-/ Videoberatung. Kostenlos für alle Pflegegrade – direkt online buchbar.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => {
                    setShowBooking(true);
                    scrollToSection('booking');
                  }}
                  className="bg-[#40E0D0] text-slate-900 hover:bg-[#3BCCC0] font-bold px-8 py-4 rounded-full text-lg shadow-md btn-primary-hover"
                  data-testid="hero-cta-button"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Termin buchen
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => scrollToSection('info')}
                  className="border-2 border-slate-200 hover:border-[#40E0D0] hover:text-[#40E0D0] font-semibold px-8 py-4 rounded-full text-lg"
                  data-testid="hero-info-button"
                >
                  Mehr erfahren
                </Button>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-6 mt-10 pt-8 border-t border-slate-200">
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Kostenlos mit Pflegegrad</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Sachsenweit</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Zertifizierter Pflegedienst</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="order-1 md:order-2 fade-in">
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1664382950513-939431ef4e5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMG1pZGRsZSUyMGFnZWQlMjB3b21hbiUyMHBob25lJTIwY29udmVyc2F0aW9uJTIwc21pbGluZyUyMGJyaWdodHxlbnwwfHx8fDE3NzExNzU0ODZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Pflegeberaterin am Telefon"
                  className="w-full h-auto rounded-2xl shadow-2xl object-cover"
                />
                <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-xl shadow-lg hidden sm:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#E0F7FA] rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-[#40E0D0]" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Telefon-/ Videoberatung</p>
                      <p className="font-bold text-slate-900">Mo-Fr, 8-18 Uhr</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="flex justify-center mt-12 animate-bounce">
          <button 
            onClick={() => scrollToSection('info')}
            className="p-2 rounded-full bg-white shadow-md"
            aria-label="Nach unten scrollen"
          >
            <ChevronDown className="w-6 h-6 text-slate-400" />
          </button>
        </div>
      </section>

      {/* Large Logo Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center">
            <img 
              src="/logo-transparent.png" 
              alt="Großstadtherzen Logo" 
              className="h-48 md:h-64 lg:h-80 w-auto mb-6"
            />
            <p className="font-['Merriweather'] text-2xl md:text-3xl font-bold text-slate-900 text-center">
              Ambulante Pflege Großstadtherzen<sup>®</sup>
            </p>
            <p className="text-lg text-slate-600 mt-2">
              Ihr Partner für Pflegeberatung in Sachsen
            </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section id="info" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-['Merriweather'] text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Was ist die Pflegeberatung nach §37,3 SGB XI?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Die Pflegeberatung ist ein gesetzlich vorgeschriebener Beratungsbesuch für Pflegebedürftige, 
              die Pflegegeld beziehen. Wir bieten diese Beratung bequem per Telefon an.
            </p>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            <div className="info-card bg-[#FAFAFA] rounded-2xl p-8 border border-slate-100" data-testid="info-card-1">
              <div className="w-14 h-14 bg-[#E0F7FA] rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-[#40E0D0]" />
              </div>
              <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-3">
                Gesetzliche Pflicht
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Pflegegeldempfänger müssen regelmäßig einen Beratungsbesuch nachweisen: 
                <strong> halbjährlich bei Pflegegrad 2-3</strong> und 
                <strong> vierteljährlich bei Pflegegrad 4-5</strong>.
              </p>
            </div>

            <div className="info-card bg-[#FAFAFA] rounded-2xl p-8 border border-slate-100" data-testid="info-card-2">
              <div className="w-14 h-14 bg-[#E0F7FA] rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-[#40E0D0]" />
              </div>
              <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-3">
                Kostenlose Beratung
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Die Beratung ist für Sie <strong>komplett kostenlos</strong>. Die Kosten werden von Ihrer 
                Pflegekasse übernommen. Sie erhalten professionelle Unterstützung ohne zusätzliche Ausgaben.
              </p>
            </div>

            <div className="info-card bg-[#FAFAFA] rounded-2xl p-8 border border-slate-100" data-testid="info-card-3">
              <div className="w-14 h-14 bg-[#E0F7FA] rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-[#40E0D0]" />
              </div>
              <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-3">
                Individuelle Betreuung
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Unsere erfahrenen Pflegefachkräfte beraten Sie zu allen Themen rund um die Pflege: 
                <strong> Hilfsmittel, Pflegetechniken, Entlastungsangebote</strong> und mehr.
              </p>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-12 md:mt-16 bg-[#E0F7FA] rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-['Merriweather'] text-2xl font-bold text-slate-900 mb-4">
                  Warum Telefon-/ Videoberatung?
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Kein Hausbesuch nötig – bleiben Sie bequem Zuhause</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Flexible Terminwahl von Montag bis Freitag</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Wir rufen Sie an – keine Wartezeiten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">Verfügbar für ganz Sachsen</span>
                  </li>
                </ul>
              </div>
              <div className="hidden md:block">
                <img 
                  src="https://images.unsplash.com/photo-1758691031979-6128d4029604?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGVsZGVybHklMjBwZXJzb24lMjB0YWxraW5nJTIwb24lMjBwaG9uZSUyMGhvbWV8ZW58MHx8fHwxNzcxMTcyMjM4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Zufriedene Seniorin am Telefon"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-16 md:py-24 section-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Merriweather'] text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Jetzt Termin vereinbaren
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Buchen Sie Ihren persönlichen Beratungstermin in wenigen Schritten. 
              Wir rufen Sie zur vereinbarten Zeit an.
            </p>
          </div>

          <BookingWizard isOpen={showBooking} onOpenChange={setShowBooking} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Merriweather'] text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Kontakt
            </h2>
            <p className="text-lg text-slate-600">
              Haben Sie Fragen? Wir sind für Sie da.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-[#FAFAFA] rounded-2xl" data-testid="contact-phone">
              <div className="w-16 h-16 bg-[#E0F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-[#40E0D0]" />
              </div>
              <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-2">Telefon</h3>
              <a href="tel:034124927833" className="text-lg text-[#40E0D0] hover:underline font-medium">
                0341-24927833
              </a>
            </div>

            <div className="text-center p-8 bg-[#FAFAFA] rounded-2xl" data-testid="contact-email">
              <div className="w-16 h-16 bg-[#E0F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-[#40E0D0]" />
              </div>
              <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-2">E-Mail</h3>
              <a href="mailto:info@grossstadtherzen.de" className="text-lg text-[#40E0D0] hover:underline font-medium">
                info@grossstadtherzen.de
              </a>
            </div>

            <div className="text-center p-8 bg-[#FAFAFA] rounded-2xl" data-testid="contact-address">
              <div className="w-16 h-16 bg-[#E0F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-[#40E0D0]" />
              </div>
              <h3 className="font-['Merriweather'] text-xl font-bold text-slate-900 mb-2">Adresse</h3>
              <p className="text-slate-600">
                Zschochersche Str. 49<br />
                04229 Leipzig
              </p>
            </div>
          </div>

          {/* Opening hours */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-[#E0F7FA] px-6 py-3 rounded-full">
              <Clock className="w-5 h-5 text-[#40E0D0]" />
              <span className="text-slate-700 font-medium">
                Beratungszeiten: Mo-Fr, 8:00 - 18:00 Uhr
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#40E0D0] text-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="flex items-center gap-4">
              <div className="bg-white rounded-full p-2">
                <img 
                  src="/logo-transparent.png" 
                  alt="Großstadtherzen Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <div>
                <p className="font-['Merriweather'] font-bold text-lg text-slate-900">
                  Ambulante Pflege Großstadtherzen<sup>®</sup>
                </p>
                <p className="text-slate-700 text-sm">Zertifizierter Pflegedienst in Leipzig</p>
              </div>
            </div>
            <div className="md:text-right">
              <div className="flex gap-6 justify-start md:justify-end mb-3">
                <a 
                  href="https://www.grossstadtherzen.de/Standort/Startseite-Leipzig/Impressum/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-700 hover:text-slate-900 text-sm transition-colors font-medium"
                  data-testid="link-impressum"
                >
                  Impressum
                </a>
                <a 
                  href="/datenschutz"
                  className="text-slate-700 hover:text-slate-900 text-sm transition-colors font-medium"
                  data-testid="link-datenschutz"
                >
                  Datenschutz
                </a>
                <a 
                  href="/admin"
                  className="text-[#40E0D0] hover:text-[#40E0D0] text-sm transition-colors font-medium select-none"
                  data-testid="link-admin"
                  onClick={(e) => {
                    e.preventDefault();
                    sessionStorage.setItem('admin_access', 'granted');
                    window.location.href = '/admin';
                  }}
                  aria-hidden="true"
                  tabIndex={-1}
                  title=""
                >
                  ·
                </a>
              </div>
              <p className="text-slate-700 text-sm">
                © {new Date().getFullYear()} Ambulante Pflege Großstadtherzen<sup>®</sup>. Alle Rechte vorbehalten.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
